import { useEffect } from "react";

const SITE_URL = "https://childrens-hour.com";
const DEFAULT_TITLE = "The Children's Hour Academy | SPARK 5-Star Early Childhood Education";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;

interface SEOHeadProps {
  title: string;
  description: string;
  canonicalPath?: string;
  /** Absolute URL (or site-relative path) for og:image / twitter:image overrides. */
  ogImage?: string;
  /** Open Graph type, defaults to "website". */
  ogType?: string;
}

const upsertMeta = (attr: "name" | "property", key: string, content: string) => {
  let tag = document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
};

const upsertLink = (rel: string, href: string) => {
  let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", rel);
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
};

const SEOHead = ({ title, description, canonicalPath, ogImage, ogType = "website" }: SEOHeadProps) => {
  useEffect(() => {
    document.title = title;

    upsertMeta("name", "description", description);

    // Open Graph
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", ogType);
    upsertMeta("property", "og:site_name", "The Children's Hour Academy");

    // Twitter cards
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);

    // Image: per-page override or site-wide default
    const imageUrl = ogImage
      ? ogImage.startsWith("http")
        ? ogImage
        : `${SITE_URL}${ogImage}`
      : DEFAULT_OG_IMAGE;
    upsertMeta("property", "og:image", imageUrl);
    upsertMeta("name", "twitter:image", imageUrl);

    // Canonical + og:url
    if (canonicalPath) {
      const fullUrl = `${SITE_URL}${canonicalPath === "/" ? "/" : canonicalPath}`;
      upsertLink("canonical", fullUrl);
      upsertMeta("property", "og:url", fullUrl);
    }

    // Cleanup: restore default title on unmount
    return () => {
      document.title = DEFAULT_TITLE;
    };
  }, [title, description, canonicalPath, ogImage, ogType]);

  return null;
};

export default SEOHead;
